import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Eye, Play, RefreshCw, Receipt, Search, Copy, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
// api (default export) is the shared axios client -- used here directly
// rather than adding a diagnosticsAPI method, because frontend/src/lib/api/
// diagnostics.js is claimed by another live session's scope right now (see
// commit d05cb19f's report). GET /admin/diagnostics/gmail-receipt-reparse/
// cohort-samples is otherwise identical in shape to every other
// diagnosticsAPI.gmailReceiptReparse* call below.
import api, { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const POLL_INTERVAL_MS = 2000;
const HEARTBEAT_STALE_SECONDS = 60;

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

function secondsSince(isoString) {
  if (!isoString) return null;
  const then = new Date(isoString).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 1000));
}

// Every outcome bucket services/gmail_receipt_reparse.py can tally, Preview
// ("would_...") and Apply (actual outcome) side by side.
const BUCKET_LABELS = [
  ['resolved_amount_and_currency', 'would_resolve_amount_and_currency', 'Resolved (amount + currency)'],
  ['resolved_amount_only', 'would_resolve_amount_only', 'Resolved (amount only, no currency signal)'],
  ['unparseable', 'would_remain_unparseable', 'Still unparseable'],
  ['skipped_conflict', 'skipped_conflict', 'Skipped (conflict)'],
];

// Every outcome bucket services/gmail_receipt_paid_status_backfill.py can
// tally (Rule-1 backfill).
const PAID_STATUS_BUCKET_LABELS = [
  ['resolved_paid', 'would_resolve_paid', 'Resolved (flipped to paid)'],
  ['remained_unpaid', 'would_remain_unpaid', 'Still unpaid (no body match)'],
  ['remained_partially_paid', 'would_remain_partially_paid', 'Still partially paid (no body match)'],
  ['skipped_conflict', 'skipped_conflict', 'Skipped (conflict)'],
];

// Every outcome bucket services/gmail_receipt_amount_backfill.py can tally
// (amount backfill).
const AMOUNT_BACKFILL_BUCKET_LABELS = [
  ['resolved_amount_and_paid', 'would_resolve_amount_and_paid', 'Resolved (amount + flipped to paid)'],
  ['resolved_amount_only', 'would_resolve_amount_only', 'Resolved (amount only, already paid)'],
  ['total_only', 'would_remain_total_only', 'Still unresolved (Total only, no Amount paid line)'],
  ['no_signal', 'would_remain_no_signal', 'Still unresolved (no signal)'],
  ['skipped_conflict', 'skipped_conflict', 'Skipped (conflict)'],
];

// Every bucket services/outbound_correspondence.py can tally -- the same
// key names for both Preview and Apply (unlike the other backfills above,
// this cohort's tally keys don't change shape between the two modes).
const OUTBOUND_CORRESPONDENCE_BUCKET_LABELS = [
  ['own_domain_body_only', 'own_domain_body_only', 'Own-domain, body only (Apply population)'],
  ['own_domain_with_pdf', 'own_domain_with_pdf', 'Own-domain, has PDF (needs human look, never auto)'],
  ['external', 'external', 'External sender (untouched)'],
];

// Mirrors GmailAmountReextractionPanel.jsx's useJobPolling exactly.
function useJobPolling({ onCompleted, onFailed, onProgress, jobStatusFn = diagnosticsAPI.gmailReceiptReparseJobStatus }) {
  const [polling, setPolling] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startTimeRef = useRef(null);

  const stop = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
    startTimeRef.current = null;
    setPolling(false);
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback((jobId) => {
    stop();
    setPolling(true);
    setElapsed(0);
    startTimeRef.current = Date.now();

    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await jobStatusFn(jobId);
        if (data.status === 'completed') {
          stop();
          onCompleted(data.result);
        } else if (data.status === 'failed') {
          stop();
          onFailed(data.error, data.progress || null);
        } else if (data.status === 'running') {
          onProgress(data.progress || null);
        }
      } catch (err) {
        stop();
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stop, onCompleted, onFailed, onProgress, jobStatusFn]);

  return { polling, elapsed, start };
}

function BySenderBreakdown({ operation, bySender, testIdPrefix = 'gmail-receipt-reparse' }) {
  const entries = Object.entries(bySender || {});
  if (entries.length === 0) return null;
  return (
    <div className="text-xs space-y-1" data-testid={`${testIdPrefix}-${operation}-by-sender`}>
      <span className="text-muted-foreground">By sender:</span>
      <ul className="pl-3 space-y-0.5">
        {entries.map(([sender, counts]) => (
          <li key={sender}>
            <span className="font-medium">{sender}</span>{' '}
            {Object.entries(counts).map(([key, count]) => `${key}: ${count}`).join(', ')}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ChainSection({
  operation, title, icon, chain, progress, failed, polling, elapsed, starting, stopping,
  onRun, onStop, runLabel, runDisabled, testIdPrefix = 'gmail-receipt-reparse', bucketLabels = BUCKET_LABELS,
}) {
  const running = chain?.status === 'running';
  const live = progress || chain?.seed;
  const cohortCount = chain?.metadata?.cohort_count;
  const processed = live?.cohort_processed;
  const heartbeatAge = secondsSince(chain?.heartbeat_at);
  const stalled = running && !polling && heartbeatAge != null && heartbeatAge > HEARTBEAT_STALE_SECONDS;
  const tallies = live?.tallies || {};
  const bySender = live?.by_sender || {};
  const errorText = (chain?.status === 'error' && chain?.error) || failed;

  return (
    <div className="border rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium flex items-center gap-1">{icon}{title}</span>
        <Button data-testid={`${testIdPrefix}-${operation}-run`} onClick={onRun} disabled={runDisabled} size="sm">
          {(starting || polling) ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
          {running
            ? `Chain running, run ${fmtInt(chain.runs_count)}, ${fmtInt(processed)}/${fmtInt(cohortCount)}`
            : runLabel}
        </Button>
        {running && (
          <Button data-testid={`${testIdPrefix}-${operation}-stop`} onClick={onStop} disabled={stopping} size="sm" variant="outline">
            {stopping ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Stop Chain
          </Button>
        )}
        {polling && (
          <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
            <RefreshCw className="h-3 w-3 animate-spin" />
            {elapsed}s
          </span>
        )}
      </div>

      {chain && (
        <div className="text-xs space-y-1" data-testid={`${testIdPrefix}-${operation}-chain-summary`}>
          <div className="flex items-center gap-1">
            <Badge variant={running ? 'default' : chain.status === 'error' ? 'destructive' : 'outline'}>
              {chain.status}
            </Badge>
            <span className="text-muted-foreground">
              run {fmtInt(chain.runs_count)}
              {cohortCount != null && <> -- {fmtInt(processed)} of {fmtInt(cohortCount)} processed</>}
            </span>
            {stalled && (
              <span className="text-amber-600 dark:text-amber-400">
                last active {heartbeatAge}s ago -- watchdog will resume it
              </span>
            )}
          </div>

          {Object.keys(tallies).length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-${operation}-tallies`}>
              {bucketLabels.map(([applyKey, previewKey, label]) => {
                const key = operation === 'apply' ? applyKey : previewKey;
                const count = tallies[key];
                if (!count) return null;
                return (
                  <span key={key} className={key.includes('unparseable') || key.includes('unpaid') || key.includes('partially_paid') ? 'text-amber-600' : ''}>
                    {label}: <strong>{count}</strong>
                  </span>
                );
              })}
            </div>
          )}

          <BySenderBreakdown operation={operation} bySender={bySender} testIdPrefix={testIdPrefix} />

          {errorText && <p className="text-red-600 dark:text-red-400">{errorText}</p>}
        </div>
      )}
    </div>
  );
}

const COHORT_SAMPLES_URL = '/admin/diagnostics/gmail-receipt-reparse/cohort-samples';
const DISPLAY_BODY_LIMIT = 2000;

function formatDocForCopy(doc) {
  const attachments = (doc.attachment_filenames || []).length > 0
    ? doc.attachment_filenames.join(', ')
    : '(none)';
  return [
    `id: ${doc.id || '-'}`,
    `subject: ${doc.email_subject || ''}`,
    `attachments: ${attachments}`,
    'body:',
    doc.email_body || '',
  ].join('\n');
}

function formatSenderGroupForCopy(sender, docs) {
  return [`sender: ${sender}`, ...docs.map(formatDocForCopy)].join('\n\n---\n\n');
}

function formatAllGroupsForCopy(samplesBySender) {
  return Object.entries(samplesBySender || {})
    .map(([sender, docs]) => formatSenderGroupForCopy(sender, docs))
    .join('\n\n===\n\n');
}

function CopyButton({ getText, label, testId }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(getText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Could not copy to clipboard: ' + getErrorMessage(err));
    }
  };
  return (
    <Button data-testid={testId} onClick={onClick} size="sm" variant="outline">
      <Copy className="h-3 w-3 mr-1" />
      {copied ? 'Copied!' : label}
    </Button>
  );
}

function CohortSampleDoc({ doc }) {
  const body = doc.email_body || '';
  const truncated = body.length > DISPLAY_BODY_LIMIT;
  const shown = truncated ? body.slice(0, DISPLAY_BODY_LIMIT) + '\n... (truncated for display -- Copy uses the full body)' : body;
  return (
    <div className="border rounded p-2 space-y-1 text-xs">
      <div className="flex flex-wrap gap-2 text-muted-foreground">
        <span>id: <span className="font-mono">{doc.id}</span></span>
        {doc.invoice_date && <span>date: {doc.invoice_date}</span>}
      </div>
      <div><span className="text-muted-foreground">subject:</span> {doc.email_subject || '(none)'}</div>
      <div>
        <span className="text-muted-foreground">attachments:</span>{' '}
        {(doc.attachment_filenames || []).length > 0 ? doc.attachment_filenames.join(', ') : '(none)'}
      </div>
      <div>
        <span className="text-muted-foreground">body:</span>
        <pre className="whitespace-pre-wrap break-words bg-muted/40 rounded p-1 mt-1">{shown || '(empty)'}</pre>
      </div>
    </div>
  );
}

function CohortSamplesSection() {
  const [open, setOpen] = useState(false);
  const [senderFilter, setSenderFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = senderFilter.trim() ? { sender_contains: senderFilter.trim() } : {};
      const { data } = await api.get(COHORT_SAMPLES_URL, { params });
      setResult(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const senderEntries = Object.entries(result?.samples_by_sender || {});

  return (
    <div className="border rounded-lg p-3 space-y-2">
      <button
        type="button"
        data-testid="gmail-receipt-reparse-cohort-samples-toggle"
        className="flex items-center gap-1 text-sm font-medium"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Cohort samples (evidence)
      </button>

      {open && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Read-only. Groups the cohort's own stored subject/body/attachment-filename fields by
            sender, straight out of the database -- no Gmail/Drive/LLM call, never writes. Pull real
            evidence here and paste it (Copy buttons below) into the next parser-template prompt
            instead of guessing a vendor's email shape.
          </p>

          <div className="flex items-center gap-2 flex-wrap">
            <Input
              data-testid="gmail-receipt-reparse-cohort-samples-filter"
              placeholder="sender contains... (e.g. apify, emergent, xero, instantly, zapmail)"
              value={senderFilter}
              onChange={(e) => setSenderFilter(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
              className="max-w-sm"
            />
            <Button data-testid="gmail-receipt-reparse-cohort-samples-load" onClick={load} disabled={loading} size="sm">
              {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Search className="h-3 w-3 mr-1" />}
              Load
            </Button>
            {senderEntries.length > 0 && (
              <CopyButton
                testId="gmail-receipt-reparse-cohort-samples-copy-all"
                label="Copy all"
                getText={() => formatAllGroupsForCopy(result.samples_by_sender)}
              />
            )}
          </div>

          {error && <p className="text-red-600 dark:text-red-400">{error}</p>}

          {result && (
            <div className="space-y-3" data-testid="gmail-receipt-reparse-cohort-samples-result">
              <p className="text-xs text-muted-foreground">
                {fmtInt(result.scanned)} doc(s) scanned across {senderEntries.length} sender(s)
                {result.scan_capped && ' -- scan capped, narrow the filter for full coverage'}.
              </p>
              {senderEntries.map(([sender, docs]) => (
                <div key={sender} className="space-y-2" data-testid="gmail-receipt-reparse-cohort-samples-group">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium">{sender}</span>
                    <Badge variant="outline">{fmtInt(result.sender_counts[sender])}</Badge>
                    <CopyButton
                      testId={`gmail-receipt-reparse-cohort-samples-copy-${sender}`}
                      label="Copy sender group"
                      getText={() => formatSenderGroupForCopy(sender, docs)}
                    />
                  </div>
                  <div className="space-y-1 pl-2">
                    {docs.map((doc) => <CohortSampleDoc key={doc.id} doc={doc} />)}
                  </div>
                </div>
              ))}
              {senderEntries.length === 0 && (
                <p className="text-xs text-muted-foreground">No matching cohort docs.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PaidStatusBackfillSection() {
  const [previewChain, setPreviewChain] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(null);
  const [previewStarting, setPreviewStarting] = useState(false);
  const [previewStopping, setPreviewStopping] = useState(false);

  const [applyChain, setApplyChain] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);
  const [applyStarting, setApplyStarting] = useState(false);
  const [applyStopping, setApplyStopping] = useState(false);

  const fetchPreviewChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.gmailReceiptPaidStatusBackfillPreviewChainStatus();
      setPreviewChain(data?.chain || null);
    } catch {
      // Non-fatal: the chain summary just won't render/update this tick.
    }
  }, []);

  const fetchApplyChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.gmailReceiptPaidStatusBackfillApplyChainStatus();
      setApplyChain(data?.chain || null);
    } catch {
      // Non-fatal.
    }
  }, []);

  const previewPoll = useJobPolling({
    jobStatusFn: diagnosticsAPI.gmailReceiptPaidStatusBackfillJobStatus,
    onCompleted: useCallback((result) => {
      setPreviewProgress(null);
      setPreviewFailed(null);
      fetchPreviewChain();
      toast.success(result?.truncated ? 'Preview run complete -- continuing chain' : 'Preview complete');
    }, [fetchPreviewChain]),
    onFailed: useCallback((error, lastProgress) => {
      setPreviewProgress((prev) => lastProgress ?? prev);
      setPreviewFailed(error);
      fetchPreviewChain();
      toast.error(error ?? 'Preview run failed');
    }, [fetchPreviewChain]),
    onProgress: useCallback((progress) => setPreviewProgress(progress), []),
  });

  const applyPoll = useJobPolling({
    jobStatusFn: diagnosticsAPI.gmailReceiptPaidStatusBackfillJobStatus,
    onCompleted: useCallback((result) => {
      setApplyProgress(null);
      setApplyFailed(null);
      fetchApplyChain();
      toast.success(result?.truncated ? 'Apply run complete -- continuing chain' : 'Apply complete');
    }, [fetchApplyChain]),
    onFailed: useCallback((error, lastProgress) => {
      setApplyProgress((prev) => lastProgress ?? prev);
      setApplyFailed(error);
      fetchApplyChain();
      toast.error(error ?? 'Apply run failed');
    }, [fetchApplyChain]),
    onProgress: useCallback((progress) => setApplyProgress(progress), []),
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.gmailReceiptPaidStatusBackfillJobLatest('preview');
        const job = data?.job;
        if (job?.status === 'running') {
          setPreviewProgress(job.progress || null);
          previewPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setPreviewProgress(job.progress || null);
          setPreviewFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.gmailReceiptPaidStatusBackfillJobLatest('apply');
        const job = data?.job;
        if (job?.status === 'running') {
          setApplyProgress(job.progress || null);
          applyPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setApplyProgress(job.progress || null);
          setApplyFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchPreviewChain(); }, [fetchPreviewChain]);
  useEffect(() => { fetchApplyChain(); }, [fetchApplyChain]);

  useEffect(() => {
    if (previewChain?.status !== 'running') return undefined;
    const id = setInterval(fetchPreviewChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [previewChain?.status, fetchPreviewChain]);

  useEffect(() => {
    if (applyChain?.status !== 'running') return undefined;
    const id = setInterval(fetchApplyChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [applyChain?.status, fetchApplyChain]);

  const runPreview = async () => {
    setPreviewStarting(true);
    setPreviewFailed(null);
    try {
      const { data } = await diagnosticsAPI.gmailReceiptPaidStatusBackfillPreviewChainStart();
      if (data.job_id) {
        previewPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('A preview chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStarting(false);
    }
  };

  const runApply = async () => {
    const cohortCount = previewChain?.metadata?.cohort_count;
    if (!window.confirm(
      `Re-check ${cohortCount ?? 'the'} Unpaid/partially-paid Gmail receipt(s) against the Stripe receipt body template? ` +
      'A matching "Amount paid" body flips payment_status to paid -- amount, currency, and review_status are never touched.'
    )) return;
    setApplyStarting(true);
    setApplyFailed(null);
    try {
      const { data } = await diagnosticsAPI.gmailReceiptPaidStatusBackfillApplyChainStart();
      if (data.job_id) {
        applyPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('An apply chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchApplyChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyStarting(false);
    }
  };

  const stopPreviewChain = async () => {
    if (!previewChain?._id) return;
    if (!window.confirm('Stop the running Preview chain? The current run finishes its time window normally.')) return;
    setPreviewStopping(true);
    try {
      await diagnosticsAPI.gmailReceiptPaidStatusBackfillPreviewChainStop(previewChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStopping(false);
    }
  };

  const stopApplyChain = async () => {
    if (!applyChain?._id) return;
    if (!window.confirm('Stop the running Apply chain? The current run finishes its time window normally.')) return;
    setApplyStopping(true);
    try {
      await diagnosticsAPI.gmailReceiptPaidStatusBackfillApplyChainStop(applyChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchApplyChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyStopping(false);
    }
  };

  const previewComplete = previewChain?.status === 'complete';

  return (
    <div className="border-t pt-3 space-y-3" data-testid="gmail-receipt-paid-status-backfill-section">
      <div>
        <h4 className="text-sm font-medium">Rule-1 backfill: receipts stranded Unpaid</h4>
        <p className="text-xs text-muted-foreground">
          Same cohort as the census's "Receipt docs left Unpaid" count (document_type=="receipt" and
          payment_status != "paid"): docs ingested before the ingestion-time fix re-checked against the
          same Stripe "Amount paid" body signal. A match flips payment_status to paid only -- amount,
          currency, and review_status are never touched. Non-matching docs stay untouched and are listed
          by sender.
        </p>
      </div>
      <ChainSection
        operation="preview"
        title="Preview"
        icon={<Eye className="h-3 w-3" />}
        chain={previewChain}
        progress={previewProgress}
        failed={previewFailed}
        polling={previewPoll.polling}
        elapsed={previewPoll.elapsed}
        starting={previewStarting}
        stopping={previewStopping}
        onRun={runPreview}
        onStop={stopPreviewChain}
        runLabel="Preview"
        runDisabled={previewStarting || previewPoll.polling || previewChain?.status === 'running'}
        testIdPrefix="gmail-receipt-paid-status-backfill"
        bucketLabels={PAID_STATUS_BUCKET_LABELS}
      />
      <ChainSection
        operation="apply"
        title="Apply"
        icon={<Play className="h-3 w-3" />}
        chain={applyChain}
        progress={applyProgress}
        failed={applyFailed}
        polling={applyPoll.polling}
        elapsed={applyPoll.elapsed}
        starting={applyStarting}
        stopping={applyStopping}
        onRun={runApply}
        onStop={stopApplyChain}
        runLabel={previewComplete ? `Apply (${fmtInt(previewChain?.metadata?.cohort_count)} candidates)` : 'Apply'}
        runDisabled={applyStarting || applyPoll.polling || applyChain?.status === 'running' || !previewComplete}
        testIdPrefix="gmail-receipt-paid-status-backfill"
        bucketLabels={PAID_STATUS_BUCKET_LABELS}
      />
      {!previewComplete && (
        <p className="text-xs text-muted-foreground">
          Apply is unavailable until this backfill's own Preview chain over the current cohort completes.
        </p>
      )}
    </div>
  );
}

function formatResidueSampleForCopy(sender, doc) {
  return [`sender: ${sender}`, `outcome: ${doc.outcome}`, 'body:', doc.email_body || ''].join('\n');
}

function formatAllResidueSamplesForCopy(samplesBySender) {
  return Object.entries(samplesBySender || {})
    .map(([sender, doc]) => formatResidueSampleForCopy(sender, doc))
    .join('\n\n===\n\n');
}

function ResidueSampleDoc({ doc }) {
  const body = doc.email_body || '';
  const truncated = body.length > DISPLAY_BODY_LIMIT;
  const shown = truncated ? body.slice(0, DISPLAY_BODY_LIMIT) + '\n... (truncated for display -- Copy uses the full body)' : body;
  return (
    <div className="border rounded p-2 space-y-1 text-xs">
      <div className="flex flex-wrap gap-2 text-muted-foreground">
        <span>id: <span className="font-mono">{doc.id}</span></span>
        {doc.old_payment_status && <span>payment_status: {doc.old_payment_status}</span>}
        <span>outcome: {doc.outcome}</span>
      </div>
      <div><span className="text-muted-foreground">subject:</span> {doc.email_subject || '(none)'}</div>
      <div>
        <span className="text-muted-foreground">body (headers stripped):</span>
        <pre className="whitespace-pre-wrap break-words bg-muted/40 rounded p-1 mt-1">{shown || '(empty)'}</pre>
      </div>
    </div>
  );
}

function ResidueSamplesSection() {
  const [open, setOpen] = useState(false);
  const [senderFilter, setSenderFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await diagnosticsAPI.gmailReceiptAmountBackfillResidueSamples(senderFilter.trim() || undefined);
      setResult(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const senderEntries = Object.entries(result?.samples_by_sender || {});

  return (
    <div className="border rounded-lg p-3 space-y-2">
      <button
        type="button"
        data-testid="gmail-receipt-amount-backfill-residue-samples-toggle"
        className="flex items-center gap-1 text-sm font-medium"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Residue samples (non-Stripe senders)
      </button>

      {open && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Read-only. One stored body per sender (headers stripped), straight out of the database, for
            the docs the amount backfill above could never resolve (no "Amount paid" body match) -- pull
            real evidence here and paste it (Copy buttons below) into the next parser-template prompt
            instead of guessing a vendor's email shape.
          </p>

          <div className="flex items-center gap-2 flex-wrap">
            <Input
              data-testid="gmail-receipt-amount-backfill-residue-samples-filter"
              placeholder="sender contains... (e.g. webflow, aircall, hubspot, pricelabs)"
              value={senderFilter}
              onChange={(e) => setSenderFilter(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
              className="max-w-sm"
            />
            <Button data-testid="gmail-receipt-amount-backfill-residue-samples-load" onClick={load} disabled={loading} size="sm">
              {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Search className="h-3 w-3 mr-1" />}
              Load
            </Button>
            {senderEntries.length > 0 && (
              <CopyButton
                testId="gmail-receipt-amount-backfill-residue-samples-copy-all"
                label="Copy all"
                getText={() => formatAllResidueSamplesForCopy(result.samples_by_sender)}
              />
            )}
          </div>

          {error && <p className="text-red-600 dark:text-red-400">{error}</p>}

          {result && (
            <div className="space-y-3" data-testid="gmail-receipt-amount-backfill-residue-samples-result">
              <p className="text-xs text-muted-foreground">
                {fmtInt(result.scanned)} doc(s) scanned across {senderEntries.length} sender(s)
                {result.scan_capped && ' -- scan capped, narrow the filter for full coverage'}.
              </p>
              {senderEntries.map(([sender, doc]) => (
                <div key={sender} className="space-y-2" data-testid="gmail-receipt-amount-backfill-residue-samples-group">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium">{sender}</span>
                    <Badge variant="outline">{fmtInt(result.sender_counts[sender])}</Badge>
                    <CopyButton
                      testId={`gmail-receipt-amount-backfill-residue-samples-copy-${sender}`}
                      label="Copy body"
                      getText={() => formatResidueSampleForCopy(sender, doc)}
                    />
                  </div>
                  <ResidueSampleDoc doc={doc} />
                </div>
              ))}
              {senderEntries.length === 0 && (
                <p className="text-xs text-muted-foreground">No matching residue docs.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const INVOICE_ZERO_AMOUNT_SAMPLES_URL = '/admin/diagnostics/gmail-receipt-reparse/invoice-zero-amount-samples';

function formatInvoiceZeroAmountSampleForCopy(sender, doc) {
  return [`sender: ${sender}`, 'body:', doc.email_body || ''].join('\n');
}

function formatAllInvoiceZeroAmountSamplesForCopy(samplesBySender) {
  return Object.entries(samplesBySender || {})
    .map(([sender, doc]) => formatInvoiceZeroAmountSampleForCopy(sender, doc))
    .join('\n\n===\n\n');
}

function InvoiceZeroAmountSampleDoc({ doc }) {
  const body = doc.email_body || '';
  const truncated = body.length > DISPLAY_BODY_LIMIT;
  const shown = truncated ? body.slice(0, DISPLAY_BODY_LIMIT) + '\n... (truncated for display -- Copy uses the full body)' : body;
  return (
    <div className="border rounded p-2 space-y-1 text-xs">
      <div className="flex flex-wrap gap-2 text-muted-foreground">
        <span>id: <span className="font-mono">{doc.id}</span></span>
        {doc.review_status && <span>review_status: {doc.review_status}</span>}
      </div>
      <div><span className="text-muted-foreground">subject:</span> {doc.email_subject || '(none)'}</div>
      <div>
        <span className="text-muted-foreground">body (headers stripped):</span>
        <pre className="whitespace-pre-wrap break-words bg-muted/40 rounded p-1 mt-1">{shown || '(empty)'}</pre>
      </div>
    </div>
  );
}

function InvoiceZeroAmountSamplesSection() {
  const [open, setOpen] = useState(false);
  const [senderFilter, setSenderFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = senderFilter.trim() ? { sender_contains: senderFilter.trim() } : {};
      const { data } = await api.get(INVOICE_ZERO_AMOUNT_SAMPLES_URL, { params });
      setResult(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const senderEntries = Object.entries(result?.samples_by_sender || {});
  const senderCounts = result?.sender_counts || {};
  const noBodyCounts = result?.sender_no_body_counts || {};
  const pdfCounts = result?.sender_pdf_counts || {};
  const noPdfCounts = result?.sender_no_pdf_counts || {};
  // Senders whose docs are entirely no-body still need to be surfaced (they have no sample to show).
  const noBodyOnlySenders = Object.keys(noBodyCounts).filter((sender) => !result?.samples_by_sender?.[sender]);

  return (
    <div className="border-t pt-3 space-y-3" data-testid="invoice-zero-amount-samples-section">
      <button
        type="button"
        data-testid="invoice-zero-amount-samples-toggle"
        className="flex items-center gap-1 text-sm font-medium"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Zero-amount invoices (samples)
      </button>

      {open && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Read-only. document_type=="invoice" docs with amount 0/null/missing -- a separate
            population from the receipt-scoped amount backfill above, which has already drained its
            cohort. Groups the cohort's own stored body by sender (headers stripped), plus a per-sender
            no-body count (a doc with no stored body can never be parser-cured and belongs on a manual
            worklist) and PDF-attachment count (an alternative amount source via the pdfplumber
            extraction route). Pull real evidence here and paste it (Copy buttons below) into the next
            parser-template prompt instead of guessing a vendor's email shape.
          </p>

          <div className="flex items-center gap-2 flex-wrap">
            <Input
              data-testid="invoice-zero-amount-samples-filter"
              placeholder="sender contains..."
              value={senderFilter}
              onChange={(e) => setSenderFilter(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
              className="max-w-sm"
            />
            <Button data-testid="invoice-zero-amount-samples-load" onClick={load} disabled={loading} size="sm">
              {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Search className="h-3 w-3 mr-1" />}
              Load
            </Button>
            {senderEntries.length > 0 && (
              <CopyButton
                testId="invoice-zero-amount-samples-copy-all"
                label="Copy all"
                getText={() => formatAllInvoiceZeroAmountSamplesForCopy(result.samples_by_sender)}
              />
            )}
          </div>

          {error && <p className="text-red-600 dark:text-red-400">{error}</p>}

          {result && (
            <div className="space-y-3" data-testid="invoice-zero-amount-samples-result">
              <p className="text-xs text-muted-foreground">
                {fmtInt(result.scanned)} doc(s) scanned across {Object.keys(senderCounts).length} sender(s)
                {result.scan_capped && ' -- scan capped, narrow the filter for full coverage'}.
              </p>
              {senderEntries.map(([sender, doc]) => (
                <div key={sender} className="space-y-2" data-testid="invoice-zero-amount-samples-group">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium">{sender}</span>
                    <Badge variant="outline">{fmtInt(senderCounts[sender])} total</Badge>
                    {noBodyCounts[sender] > 0 && (
                      <Badge variant="outline" className="text-amber-600">{fmtInt(noBodyCounts[sender])} no body</Badge>
                    )}
                    <Badge variant="outline">{fmtInt(pdfCounts[sender] || 0)} w/ PDF</Badge>
                    <Badge variant="outline">{fmtInt(noPdfCounts[sender] || 0)} w/o PDF</Badge>
                    <CopyButton
                      testId={`invoice-zero-amount-samples-copy-${sender}`}
                      label="Copy body"
                      getText={() => formatInvoiceZeroAmountSampleForCopy(sender, doc)}
                    />
                  </div>
                  <InvoiceZeroAmountSampleDoc doc={doc} />
                </div>
              ))}
              {noBodyOnlySenders.map((sender) => (
                <div key={sender} className="text-xs space-x-2" data-testid="invoice-zero-amount-samples-no-body-only-group">
                  <span className="font-medium">{sender}</span>
                  <Badge variant="outline">{fmtInt(senderCounts[sender])} total</Badge>
                  <span className="text-amber-600">{fmtInt(noBodyCounts[sender])} no body -- no sample available, manual worklist</span>
                </div>
              ))}
              {senderEntries.length === 0 && noBodyOnlySenders.length === 0 && (
                <p className="text-xs text-muted-foreground">No matching zero-amount invoice docs.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AmountBackfillSection() {
  const [previewChain, setPreviewChain] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(null);
  const [previewStarting, setPreviewStarting] = useState(false);
  const [previewStopping, setPreviewStopping] = useState(false);

  const [applyChain, setApplyChain] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);
  const [applyStarting, setApplyStarting] = useState(false);
  const [applyStopping, setApplyStopping] = useState(false);

  const fetchPreviewChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.gmailReceiptAmountBackfillPreviewChainStatus();
      setPreviewChain(data?.chain || null);
    } catch {
      // Non-fatal: the chain summary just won't render/update this tick.
    }
  }, []);

  const fetchApplyChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.gmailReceiptAmountBackfillApplyChainStatus();
      setApplyChain(data?.chain || null);
    } catch {
      // Non-fatal.
    }
  }, []);

  const previewPoll = useJobPolling({
    jobStatusFn: diagnosticsAPI.gmailReceiptAmountBackfillJobStatus,
    onCompleted: useCallback((result) => {
      setPreviewProgress(null);
      setPreviewFailed(null);
      fetchPreviewChain();
      toast.success(result?.truncated ? 'Preview run complete -- continuing chain' : 'Preview complete');
    }, [fetchPreviewChain]),
    onFailed: useCallback((error, lastProgress) => {
      setPreviewProgress((prev) => lastProgress ?? prev);
      setPreviewFailed(error);
      fetchPreviewChain();
      toast.error(error ?? 'Preview run failed');
    }, [fetchPreviewChain]),
    onProgress: useCallback((progress) => setPreviewProgress(progress), []),
  });

  const applyPoll = useJobPolling({
    jobStatusFn: diagnosticsAPI.gmailReceiptAmountBackfillJobStatus,
    onCompleted: useCallback((result) => {
      setApplyProgress(null);
      setApplyFailed(null);
      fetchApplyChain();
      toast.success(result?.truncated ? 'Apply run complete -- continuing chain' : 'Apply complete');
    }, [fetchApplyChain]),
    onFailed: useCallback((error, lastProgress) => {
      setApplyProgress((prev) => lastProgress ?? prev);
      setApplyFailed(error);
      fetchApplyChain();
      toast.error(error ?? 'Apply run failed');
    }, [fetchApplyChain]),
    onProgress: useCallback((progress) => setApplyProgress(progress), []),
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.gmailReceiptAmountBackfillJobLatest('preview');
        const job = data?.job;
        if (job?.status === 'running') {
          setPreviewProgress(job.progress || null);
          previewPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setPreviewProgress(job.progress || null);
          setPreviewFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.gmailReceiptAmountBackfillJobLatest('apply');
        const job = data?.job;
        if (job?.status === 'running') {
          setApplyProgress(job.progress || null);
          applyPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setApplyProgress(job.progress || null);
          setApplyFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchPreviewChain(); }, [fetchPreviewChain]);
  useEffect(() => { fetchApplyChain(); }, [fetchApplyChain]);

  useEffect(() => {
    if (previewChain?.status !== 'running') return undefined;
    const id = setInterval(fetchPreviewChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [previewChain?.status, fetchPreviewChain]);

  useEffect(() => {
    if (applyChain?.status !== 'running') return undefined;
    const id = setInterval(fetchApplyChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [applyChain?.status, fetchApplyChain]);

  const runPreview = async () => {
    setPreviewStarting(true);
    setPreviewFailed(null);
    try {
      const { data } = await diagnosticsAPI.gmailReceiptAmountBackfillPreviewChainStart();
      if (data.job_id) {
        previewPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('A preview chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStarting(false);
    }
  };

  const runApply = async () => {
    const cohortCount = previewChain?.metadata?.cohort_count;
    if (!window.confirm(
      `Re-check ${cohortCount ?? 'the'} zero/null/missing-amount Gmail receipt(s) against the Stripe receipt body template? ` +
      'A matching "Amount paid" body writes amount + currency, and also flips payment_status to paid if the doc ' +
      'isn\'t already paid -- review_status is never touched.'
    )) return;
    setApplyStarting(true);
    setApplyFailed(null);
    try {
      const { data } = await diagnosticsAPI.gmailReceiptAmountBackfillApplyChainStart();
      if (data.job_id) {
        applyPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('An apply chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchApplyChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyStarting(false);
    }
  };

  const stopPreviewChain = async () => {
    if (!previewChain?._id) return;
    if (!window.confirm('Stop the running Preview chain? The current run finishes its time window normally.')) return;
    setPreviewStopping(true);
    try {
      await diagnosticsAPI.gmailReceiptAmountBackfillPreviewChainStop(previewChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStopping(false);
    }
  };

  const stopApplyChain = async () => {
    if (!applyChain?._id) return;
    if (!window.confirm('Stop the running Apply chain? The current run finishes its time window normally.')) return;
    setApplyStopping(true);
    try {
      await diagnosticsAPI.gmailReceiptAmountBackfillApplyChainStop(applyChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchApplyChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyStopping(false);
    }
  };

  const previewComplete = previewChain?.status === 'complete';

  return (
    <div className="border-t pt-3 space-y-3" data-testid="gmail-receipt-amount-backfill-section">
      <div>
        <h4 className="text-sm font-medium">Amount backfill: zero-amount receipts</h4>
        <p className="text-xs text-muted-foreground">
          document_type=="receipt" and amount in (0, null, missing), including docs the Rule-1 backfill
          above already flipped to paid. A matching "Amount paid" body writes amount + currency (from the
          amount's own symbol, never defaulted) and flips payment_status to paid in the same write if the
          doc isn't already paid. review_status is never touched. A "Total"-only body or no signal at all
          leaves the doc completely untouched and is listed by sender below.
        </p>
      </div>
      <ChainSection
        operation="preview"
        title="Preview"
        icon={<Eye className="h-3 w-3" />}
        chain={previewChain}
        progress={previewProgress}
        failed={previewFailed}
        polling={previewPoll.polling}
        elapsed={previewPoll.elapsed}
        starting={previewStarting}
        stopping={previewStopping}
        onRun={runPreview}
        onStop={stopPreviewChain}
        runLabel="Preview"
        runDisabled={previewStarting || previewPoll.polling || previewChain?.status === 'running'}
        testIdPrefix="gmail-receipt-amount-backfill"
        bucketLabels={AMOUNT_BACKFILL_BUCKET_LABELS}
      />
      <ChainSection
        operation="apply"
        title="Apply"
        icon={<Play className="h-3 w-3" />}
        chain={applyChain}
        progress={applyProgress}
        failed={applyFailed}
        polling={applyPoll.polling}
        elapsed={applyPoll.elapsed}
        starting={applyStarting}
        stopping={applyStopping}
        onRun={runApply}
        onStop={stopApplyChain}
        runLabel={previewComplete ? `Apply (${fmtInt(previewChain?.metadata?.cohort_count)} candidates)` : 'Apply'}
        runDisabled={applyStarting || applyPoll.polling || applyChain?.status === 'running' || !previewComplete}
        testIdPrefix="gmail-receipt-amount-backfill"
        bucketLabels={AMOUNT_BACKFILL_BUCKET_LABELS}
      />
      {!previewComplete && (
        <p className="text-xs text-muted-foreground">
          Apply is unavailable until this backfill's own Preview chain over the current cohort completes.
        </p>
      )}
      <ResidueSamplesSection />
    </div>
  );
}

function OutboundCorrespondenceSection() {
  const [open, setOpen] = useState(false);
  const [censusLoading, setCensusLoading] = useState(false);
  const [censusError, setCensusError] = useState(null);
  const [census, setCensus] = useState(null);
  const [censusAttempted, setCensusAttempted] = useState(false);

  const [previewChain, setPreviewChain] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(null);
  const [previewStarting, setPreviewStarting] = useState(false);
  const [previewStopping, setPreviewStopping] = useState(false);

  const [applyChain, setApplyChain] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);
  const [applyStarting, setApplyStarting] = useState(false);
  const [applyStopping, setApplyStopping] = useState(false);

  const loadCensus = useCallback(async () => {
    setCensusLoading(true);
    setCensusError(null);
    setCensusAttempted(true);
    try {
      const { data } = await diagnosticsAPI.outboundCorrespondenceCensus();
      setCensus(data);
    } catch (err) {
      setCensusError(getErrorMessage(err));
    } finally {
      setCensusLoading(false);
    }
  }, []);

  const fetchPreviewChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.outboundCorrespondencePreviewChainStatus();
      setPreviewChain(data?.chain || null);
    } catch {
      // Non-fatal: the chain summary just won't render/update this tick.
    }
  }, []);

  const fetchApplyChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.outboundCorrespondenceApplyChainStatus();
      setApplyChain(data?.chain || null);
    } catch {
      // Non-fatal.
    }
  }, []);

  const previewPoll = useJobPolling({
    jobStatusFn: diagnosticsAPI.outboundCorrespondenceJobStatus,
    onCompleted: useCallback((result) => {
      setPreviewProgress(null);
      setPreviewFailed(null);
      fetchPreviewChain();
      toast.success(result?.truncated ? 'Preview run complete -- continuing chain' : 'Preview complete');
    }, [fetchPreviewChain]),
    onFailed: useCallback((error, lastProgress) => {
      setPreviewProgress((prev) => lastProgress ?? prev);
      setPreviewFailed(error);
      fetchPreviewChain();
      toast.error(error ?? 'Preview run failed');
    }, [fetchPreviewChain]),
    onProgress: useCallback((progress) => setPreviewProgress(progress), []),
  });

  const applyPoll = useJobPolling({
    jobStatusFn: diagnosticsAPI.outboundCorrespondenceJobStatus,
    onCompleted: useCallback((result) => {
      setApplyProgress(null);
      setApplyFailed(null);
      fetchApplyChain();
      loadCensus();
      toast.success(result?.truncated ? 'Apply run complete -- continuing chain' : 'Apply complete');
    }, [fetchApplyChain, loadCensus]),
    onFailed: useCallback((error, lastProgress) => {
      setApplyProgress((prev) => lastProgress ?? prev);
      setApplyFailed(error);
      fetchApplyChain();
      toast.error(error ?? 'Apply run failed');
    }, [fetchApplyChain]),
    onProgress: useCallback((progress) => setApplyProgress(progress), []),
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.outboundCorrespondenceJobLatest('preview');
        const job = data?.job;
        if (job?.status === 'running') {
          setPreviewProgress(job.progress || null);
          previewPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setPreviewProgress(job.progress || null);
          setPreviewFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.outboundCorrespondenceJobLatest('apply');
        const job = data?.job;
        if (job?.status === 'running') {
          setApplyProgress(job.progress || null);
          applyPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setApplyProgress(job.progress || null);
          setApplyFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (open) fetchPreviewChain(); }, [open, fetchPreviewChain]);
  useEffect(() => { if (open) fetchApplyChain(); }, [open, fetchApplyChain]);
  useEffect(() => { if (open && !censusAttempted) loadCensus(); }, [open, censusAttempted, loadCensus]);

  useEffect(() => {
    if (previewChain?.status !== 'running') return undefined;
    const id = setInterval(fetchPreviewChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [previewChain?.status, fetchPreviewChain]);

  useEffect(() => {
    if (applyChain?.status !== 'running') return undefined;
    const id = setInterval(fetchApplyChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [applyChain?.status, fetchApplyChain]);

  const runPreview = async () => {
    setPreviewStarting(true);
    setPreviewFailed(null);
    try {
      const { data } = await diagnosticsAPI.outboundCorrespondencePreviewChainStart();
      if (data.job_id) {
        previewPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('A preview chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStarting(false);
    }
  };

  const runApply = async () => {
    const cohortCount = previewChain?.metadata?.cohort_count;
    if (!window.confirm(
      `Classify ${cohortCount ?? 'the'} own-domain-sender, no-PDF, amount-missing invoice-typed Gmail doc(s) as ` +
      'outbound_correspondence? Own-domain docs WITH a PDF attachment are never touched by Apply -- they stay ' +
      'listed above for a human to look at.'
    )) return;
    setApplyStarting(true);
    setApplyFailed(null);
    try {
      const { data } = await diagnosticsAPI.outboundCorrespondenceApplyChainStart();
      if (data.job_id) {
        applyPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('An apply chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchApplyChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyStarting(false);
    }
  };

  const stopPreviewChain = async () => {
    if (!previewChain?._id) return;
    if (!window.confirm('Stop the running Preview chain? The current run finishes its time window normally.')) return;
    setPreviewStopping(true);
    try {
      await diagnosticsAPI.outboundCorrespondencePreviewChainStop(previewChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStopping(false);
    }
  };

  const stopApplyChain = async () => {
    if (!applyChain?._id) return;
    if (!window.confirm('Stop the running Apply chain? The current run finishes its time window normally.')) return;
    setApplyStopping(true);
    try {
      await diagnosticsAPI.outboundCorrespondenceApplyChainStop(applyChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchApplyChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyStopping(false);
    }
  };

  const previewComplete = previewChain?.status === 'complete';
  const pdfSamples = census?.own_domain_with_pdf_samples || [];

  return (
    <div className="border-t pt-3 space-y-3" data-testid="outbound-correspondence-section">
      <button
        type="button"
        data-testid="outbound-correspondence-toggle"
        className="flex items-center gap-1 text-sm font-medium"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Outbound correspondence misclassified as invoices
      </button>

      {open && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Own-domain-sender, document_type=="invoice", amount-missing, no-PDF Gmail docs -- the operator's
            own outbound correspondence (a quote thread, fee lines read as invoice-shaped by the LLM
            classifier), never a real invoice. The conjunction is conservative: an own-domain sender with a
            PDF attached (a forwarded real vendor invoice) is never auto-classified, only listed below for a
            human to look at. See services/outbound_correspondence.py.
          </p>

          <div className="flex items-center gap-2 flex-wrap">
            <Button data-testid="outbound-correspondence-census-load" onClick={loadCensus} disabled={censusLoading} size="sm" variant="outline">
              {censusLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Search className="h-3 w-3 mr-1" />}
              Load census
            </Button>
          </div>

          {censusError && <p className="text-red-600 dark:text-red-400">{censusError}</p>}

          {census && (
            <div className="text-xs space-y-2" data-testid="outbound-correspondence-census-result">
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">{fmtInt(census.cohort_total)} cohort total</Badge>
                <Badge variant="outline">{fmtInt(census.own_domain_body_only)} own-domain, body only</Badge>
                <Badge variant="outline" className="text-amber-600">{fmtInt(census.own_domain_with_pdf)} own-domain, has PDF</Badge>
                <Badge variant="outline">{fmtInt(census.external_senders)} external sender</Badge>
                <Badge variant="outline">{fmtInt(census.already_classified_outbound_correspondence)} already classified</Badge>
              </div>
              <div className="text-muted-foreground">
                Own domains: {(census.own_domains || []).join(', ') || '(none configured)'}
              </div>
              {pdfSamples.length > 0 && (
                <div className="space-y-1" data-testid="outbound-correspondence-pdf-samples">
                  <span className="text-muted-foreground">Own-domain + PDF (needs human look, never auto-classified):</span>
                  <ul className="pl-3 space-y-0.5">
                    {pdfSamples.map((doc) => (
                      <li key={doc.id}>
                        <span className="font-mono">{doc.id}</span> -- {doc.sender} -- {doc.subject || '(no subject)'}
                        {doc.attachment_filenames?.length > 0 && <> -- {doc.attachment_filenames.join(', ')}</>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <ChainSection
            operation="preview"
            title="Preview"
            icon={<Eye className="h-3 w-3" />}
            chain={previewChain}
            progress={previewProgress}
            failed={previewFailed}
            polling={previewPoll.polling}
            elapsed={previewPoll.elapsed}
            starting={previewStarting}
            stopping={previewStopping}
            onRun={runPreview}
            onStop={stopPreviewChain}
            runLabel="Preview"
            runDisabled={previewStarting || previewPoll.polling || previewChain?.status === 'running'}
            testIdPrefix="outbound-correspondence"
            bucketLabels={OUTBOUND_CORRESPONDENCE_BUCKET_LABELS}
          />
          <ChainSection
            operation="apply"
            title="Apply"
            icon={<Play className="h-3 w-3" />}
            chain={applyChain}
            progress={applyProgress}
            failed={applyFailed}
            polling={applyPoll.polling}
            elapsed={applyPoll.elapsed}
            starting={applyStarting}
            stopping={applyStopping}
            onRun={runApply}
            onStop={stopApplyChain}
            runLabel={previewComplete ? `Apply (${fmtInt(previewChain?.metadata?.cohort_count)} candidates)` : 'Apply'}
            runDisabled={applyStarting || applyPoll.polling || applyChain?.status === 'running' || !previewComplete}
            testIdPrefix="outbound-correspondence"
            bucketLabels={OUTBOUND_CORRESPONDENCE_BUCKET_LABELS}
          />
          {!previewComplete && (
            <p className="text-xs text-muted-foreground">
              Apply is unavailable until this backfill's own Preview chain over the current cohort completes.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function GmailReceiptReparsePanel() {
  const [previewChain, setPreviewChain] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(null);
  const [previewStarting, setPreviewStarting] = useState(false);
  const [previewStopping, setPreviewStopping] = useState(false);

  const [applyChain, setApplyChain] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);
  const [applyStarting, setApplyStarting] = useState(false);
  const [applyStopping, setApplyStopping] = useState(false);

  const fetchPreviewChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.gmailReceiptReparsePreviewChainStatus();
      setPreviewChain(data?.chain || null);
    } catch {
      // Non-fatal: the chain summary just won't render/update this tick.
    }
  }, []);

  const fetchApplyChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.gmailReceiptReparseApplyChainStatus();
      setApplyChain(data?.chain || null);
    } catch {
      // Non-fatal.
    }
  }, []);

  const previewPoll = useJobPolling({
    onCompleted: useCallback((result) => {
      setPreviewProgress(null);
      setPreviewFailed(null);
      fetchPreviewChain();
      toast.success(result?.truncated ? 'Preview run complete -- continuing chain' : 'Preview complete');
    }, [fetchPreviewChain]),
    onFailed: useCallback((error, lastProgress) => {
      setPreviewProgress((prev) => lastProgress ?? prev);
      setPreviewFailed(error);
      fetchPreviewChain();
      toast.error(error ?? 'Preview run failed');
    }, [fetchPreviewChain]),
    onProgress: useCallback((progress) => setPreviewProgress(progress), []),
  });

  const applyPoll = useJobPolling({
    onCompleted: useCallback((result) => {
      setApplyProgress(null);
      setApplyFailed(null);
      fetchApplyChain();
      toast.success(result?.truncated ? 'Apply run complete -- continuing chain' : 'Apply complete');
    }, [fetchApplyChain]),
    onFailed: useCallback((error, lastProgress) => {
      setApplyProgress((prev) => lastProgress ?? prev);
      setApplyFailed(error);
      fetchApplyChain();
      toast.error(error ?? 'Apply run failed');
    }, [fetchApplyChain]),
    onProgress: useCallback((progress) => setApplyProgress(progress), []),
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.gmailReceiptReparseJobLatest('preview');
        const job = data?.job;
        if (job?.status === 'running') {
          setPreviewProgress(job.progress || null);
          previewPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setPreviewProgress(job.progress || null);
          setPreviewFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.gmailReceiptReparseJobLatest('apply');
        const job = data?.job;
        if (job?.status === 'running') {
          setApplyProgress(job.progress || null);
          applyPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setApplyProgress(job.progress || null);
          setApplyFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchPreviewChain(); }, [fetchPreviewChain]);
  useEffect(() => { fetchApplyChain(); }, [fetchApplyChain]);

  useEffect(() => {
    if (previewChain?.status !== 'running') return undefined;
    const id = setInterval(fetchPreviewChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [previewChain?.status, fetchPreviewChain]);

  useEffect(() => {
    if (applyChain?.status !== 'running') return undefined;
    const id = setInterval(fetchApplyChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [applyChain?.status, fetchApplyChain]);

  const runPreview = async () => {
    setPreviewStarting(true);
    setPreviewFailed(null);
    try {
      const { data } = await diagnosticsAPI.gmailReceiptReparsePreviewChainStart();
      if (data.job_id) {
        previewPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('A preview chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStarting(false);
    }
  };

  const runApply = async () => {
    const cohortCount = previewChain?.metadata?.cohort_count;
    if (!window.confirm(
      `Re-parse ${cohortCount ?? 'the'} quarantined/suspect Gmail receipt(s) against the Stripe receipt body template? ` +
      'Resolved docs move to pending_review with a corrected amount -- never auto-approved.'
    )) return;
    setApplyStarting(true);
    setApplyFailed(null);
    try {
      const { data } = await diagnosticsAPI.gmailReceiptReparseApplyChainStart();
      if (data.job_id) {
        applyPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('An apply chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchApplyChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyStarting(false);
    }
  };

  const stopPreviewChain = async () => {
    if (!previewChain?._id) return;
    if (!window.confirm('Stop the running Preview chain? The current run finishes its time window normally.')) return;
    setPreviewStopping(true);
    try {
      await diagnosticsAPI.gmailReceiptReparsePreviewChainStop(previewChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStopping(false);
    }
  };

  const stopApplyChain = async () => {
    if (!applyChain?._id) return;
    if (!window.confirm('Stop the running Apply chain? The current run finishes its time window normally.')) return;
    setApplyStopping(true);
    try {
      await diagnosticsAPI.gmailReceiptReparseApplyChainStop(applyChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchApplyChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyStopping(false);
    }
  };

  const previewComplete = previewChain?.status === 'complete';

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Receipt className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Gmail Receipt Re-parse (Stripe body)</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Deterministic (no-LLM) re-parse of the amount_parse_suspect / quarantined-for-amount
          population's own stored email body against the standard Stripe receipt template
          ("Amount paid $X" / "Total $X", VAT line). A subject-only fallback parse could never
          recover these -- Stripe receipt subjects never carry the amount. Currency is read from
          the amount's own symbol, never defaulted. Resolved docs move to pending_review with the
          quarantine cleared -- never auto-approved. Apply is gated behind a completed Preview
          over the same cohort.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <ChainSection
          operation="preview"
          title="Preview"
          icon={<Eye className="h-3 w-3" />}
          chain={previewChain}
          progress={previewProgress}
          failed={previewFailed}
          polling={previewPoll.polling}
          elapsed={previewPoll.elapsed}
          starting={previewStarting}
          stopping={previewStopping}
          onRun={runPreview}
          onStop={stopPreviewChain}
          runLabel="Preview"
          runDisabled={previewStarting || previewPoll.polling || previewChain?.status === 'running'}
        />
        <ChainSection
          operation="apply"
          title="Apply"
          icon={<Play className="h-3 w-3" />}
          chain={applyChain}
          progress={applyProgress}
          failed={applyFailed}
          polling={applyPoll.polling}
          elapsed={applyPoll.elapsed}
          starting={applyStarting}
          stopping={applyStopping}
          onRun={runApply}
          onStop={stopApplyChain}
          runLabel={previewComplete ? `Apply (${fmtInt(previewChain?.metadata?.cohort_count)} candidates)` : 'Apply'}
          runDisabled={applyStarting || applyPoll.polling || applyChain?.status === 'running' || !previewComplete}
        />
        {!previewComplete && (
          <p className="text-xs text-muted-foreground">
            Apply is disabled until a Preview chain over the current cohort completes.
          </p>
        )}
        <CohortSamplesSection />
        <PaidStatusBackfillSection />
        <AmountBackfillSection />
        <InvoiceZeroAmountSamplesSection />
        <OutboundCorrespondenceSection />
      </CardContent>
    </Card>
  );
}
